/**
 * The reports read-model scope.
 *
 * Every report page reads its programmes from this context. `BackendReportScope`
 * fills it from the platform client, so the reports only ever show governed
 * programmes the signed-in caller can already see in their active tenant. There
 * is no browser-side store behind it — see `./library`.
 *
 * @module reports/ReportTenantScope
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { usePlatformSession } from "@/platform";

import { type BackendProgrammeLibraryInput, toReportProgrammes } from "./library";
import type { StoredProgramme } from "./types";

export type ReportScopeStatus = "loading" | "ready" | "error";

/**
 * Which programmes the reports cover. `tenant`: every programme in the tenant,
 * as a client or platform admin sees it. `accessible`: only those the caller
 * may open, as programme visibility narrows a member.
 */
export type ReportScopeKind = "tenant" | "accessible";

export interface ReportScopeValue {
  /** Tenant the programmes were read under, or null outside a session. */
  tenantId: string | null;
  tenantName: string | null;
  scope: ReportScopeKind;
  programmes: StoredProgramme[];
  status: ReportScopeStatus;
  error: Error | null;
  refresh: () => Promise<void>;
}

const EMPTY_PROGRAMMES: StoredProgramme[] = [];

const ReportScopeContext = createContext<ReportScopeValue>({
  tenantId: null,
  tenantName: null,
  scope: "accessible",
  programmes: EMPTY_PROGRAMMES,
  status: "ready",
  error: null,
  refresh: () => Promise.resolve(),
});

interface ReportTenantScopeProps {
  tenantId: string | null;
  tenantName?: string | null;
  scope?: ReportScopeKind;
  programmes: StoredProgramme[];
  status?: ReportScopeStatus;
  error?: Error | null;
  refresh?: () => Promise<void>;
  children: React.ReactNode;
}

/**
 * Pure provider over an already-read set of programmes.
 *
 * Use `BackendReportScope` in the app. This variant exists so tests and callers
 * that already hold the records can supply them directly.
 */
export function ReportTenantScope({
  tenantId,
  tenantName = null,
  scope = "accessible",
  programmes,
  status = "ready",
  error = null,
  refresh = undefined,
  children,
}: ReportTenantScopeProps): React.JSX.Element {
  const value = useMemo<ReportScopeValue>(
    () => ({
      tenantId,
      tenantName,
      scope,
      programmes,
      status,
      error,
      refresh: refresh ?? (() => Promise.resolve()),
    }),
    [tenantId, tenantName, scope, programmes, status, error, refresh],
  );

  return <ReportScopeContext.Provider value={value}>{children}</ReportScopeContext.Provider>;
}

/**
 * Reads the active tenant's governed programmes through the platform client and
 * provides them to the report pages.
 */
export function BackendReportScope({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { client, session } = usePlatformSession();
  const tenantId = session?.tenant.id ?? null;
  const tenantName = session?.tenant.name ?? null;
  const accountRole = session?.subject.accountRole;
  const scope: ReportScopeKind =
    accountRole === "client-admin" || accountRole === "platform-admin" ? "tenant" : "accessible";
  const [programmes, setProgrammes] = useState<StoredProgramme[]>(EMPTY_PROGRAMMES);
  const [status, setStatus] = useState<ReportScopeStatus>("loading");
  const [error, setError] = useState<Error | null>(null);

  /*
   * Every read takes a ticket and only the newest one may write. A read the
   * tenant switch superseded answers later and would otherwise replace what
   * the new tenant's read had already shown, leaving the reports empty with
   * no error.
   */
  const latest = useRef(0);

  const refresh = useCallback(async (): Promise<void> => {
    latest.current += 1;
    const ticket = latest.current;
    const settle = (apply: () => void): void => {
      if (latest.current === ticket) {
        apply();
      }
    };

    if (!tenantId || tenantName === null) {
      settle(() => {
        setProgrammes(EMPTY_PROGRAMMES);
        setStatus("error");
        setError(new Error("No active tenant is available for reports"));
      });
      return;
    }

    settle(() => {
      setStatus("loading");
      setError(null);
    });
    try {
      const summaries = await client.listAccessibleProgrammes();
      const records = await Promise.all(
        summaries.map((summary) => client.loadProgramme(summary.id)),
      );
      const inputs: BackendProgrammeLibraryInput[] = records
        .filter((record) => record !== null)
        .map((record) => {
          const summary = summaries.find((programme) => programme.id === record.id);
          return {
            programme: record.structured,
            tenantId: record.tenantId,
            tenantName,
            revision: record.saveRevision,
            workflowState: record.workflowState,
            leadUserId: summary?.leadUserId,
            leadUserName: summary?.leadUserName,
            leadLastOpenedAt: summary?.leadLastOpenedAt,
          };
        });

      const read = toReportProgrammes(tenantId, inputs, new Date().toISOString());
      settle(() => {
        setProgrammes(read);
        setStatus("ready");
      });
    } catch (cause) {
      settle(() => {
        setProgrammes(EMPTY_PROGRAMMES);
        setError(cause instanceof Error ? cause : new Error("Unable to load reports"));
        setStatus("error");
      });
    }
  }, [client, tenantId, tenantName]);

  useEffect(() => {
    refresh().catch(() => {
      // `refresh` reports every failure through `status`; there is nothing
      // left to do with the rejection, and an unhandled one fails a test run
      // in whichever file happens to be running when it lands.
    });
    // Supersede whatever is in flight: the tenant changed, or the scope is gone.
    return () => {
      latest.current += 1;
    };
  }, [refresh]);

  return (
    <ReportTenantScope
      tenantId={tenantId}
      tenantName={tenantName}
      scope={scope}
      programmes={programmes}
      status={status}
      error={error}
      refresh={refresh}
    >
      {children}
    </ReportTenantScope>
  );
}

export function useReportScope(): ReportScopeValue {
  return useContext(ReportScopeContext);
}

export function useReportProgrammes(): StoredProgramme[] {
  return useContext(ReportScopeContext).programmes;
}

export function useReportTenantId(): string | null {
  return useContext(ReportScopeContext).tenantId;
}
